/**
 * Build-time feature flags. Vite's `define` injects values at compile time so
 * the bundler can tree-shake gated code when the flag is `false`.
 *
 * Steam build (offline-only):
 *   VITE_FEATURE_MULTIPLAYER=false npm run build -w packages/client
 *   VITE_FEATURE_ONLINE=false      npm run build -w packages/client  // Phase 2
 *
 * Web build (default at runesofeternal.com):
 *   npm run build -w packages/client   // both flags default to true
 *
 * In test/dev environments where Vite isn't running (vitest, ts-node, etc.)
 * the `__FEATURE_*__` globals are undefined — fallbacks default both flags
 * to `true` so existing MP/online test suites continue to exercise their
 * code paths without test-side overrides.
 */

declare const __FEATURE_MULTIPLAYER__: boolean | undefined;
declare const __FEATURE_ONLINE__: boolean | undefined;

/** When `false`, all multiplayer code paths are gated off:
 *  - SocketService never connects
 *  - MultiplayerService singleton is never instantiated
 *  - MenuScene hides MP / Coop / Social / Friends buttons
 *  - BattleScene + lobby scenes skip invite-toast wiring
 *  Phase 1 (Steam launch) sets this to `false` in the Steam build.
 */
export const FEATURE_MULTIPLAYER: boolean = (() => {
  try {
    return typeof __FEATURE_MULTIPLAYER__ === 'boolean' ? __FEATURE_MULTIPLAYER__ : true;
  } catch {
    return true;
  }
})();

/** When `false`, all server HTTP / online dependencies are gated off:
 *  - AuthService no-ops (no login screen, no JWT)
 *  - SyncService no-ops (saves persisted locally only)
 *  - ServerHealthService no-ops (no offline overlay polling)
 *  - All `fetch()` to runesofeternal.com paths are skipped
 *  Phase 2 (Steam offline-first migration) sets this to `false` for Steam
 *  builds. Phase 1 leaves it `true` — MP gating only.
 */
export const FEATURE_ONLINE: boolean = (() => {
  try {
    return typeof __FEATURE_ONLINE__ === 'boolean' ? __FEATURE_ONLINE__ : true;
  } catch {
    return true;
  }
})();
